import { all, call, put, SagaGenerator, select, takeEvery } from 'typed-redux-saga';

import { RECORD_MIME_TYPE } from '../../../pages/call/constants';
import { getRecordFileName } from '../../helpers/get-record-file-name';
import { createLogger } from '../../helpers/loggerFacade';
import { selectUserUid } from '../user/selectors';
import { ObjectStoreNames, SESSION_PREFIX } from './constants';
import fixWebmBlobDuration from './helpers/fix-webm-duration';
import { IndexedDatabase, removeDatabase } from './helpers/indexed-db';
import {
    downloadLastRecordAction,
    setHasLastRecording,
    setLastRecordingDate,
    updateHasLastRecordAction,
} from './slice';
import { RecordMetadata, RecordMetadataRecorderKind, RecordMetadataStatus } from './types';

const logger = createLogger('download-record-sagas');

type SessionWithMetadata = {
    name: string;
    metadata: RecordMetadata;
};

const isSupportedRecordStatus = (status: unknown): status is RecordMetadataStatus => {
    return status === 'recording' || status === 'recoverable' || status === 'saved' || status === 'discarded';
};

const isSupportedRecorderKind = (kind: unknown): kind is RecordMetadataRecorderKind => {
    return kind === 'indexeddb' || kind === 'pm-backup';
};

const parseSessionStartDate = (sessionName: string): number => {
    const value = Number(sessionName.replace(SESSION_PREFIX, ''));
    return Number.isFinite(value) && value > 0 ? value : Date.now();
};

const normalizeMetadata = (
    metadata: Partial<RecordMetadata> | undefined,
    sessionName: string,
    fallbackKind: RecordMetadataRecorderKind = 'indexeddb'
): RecordMetadata => {
    const startDate = typeof metadata?.startDate === 'number' ? metadata.startDate : parseSessionStartDate(sessionName);
    const duration = typeof metadata?.duration === 'number' ? metadata.duration : 0;
    const chunkCount =
        typeof metadata?.chunkCount === 'number'
            ? metadata.chunkCount
            : duration > 0
              ? Math.max(1, Math.round(duration / 1000))
              : 0;
    const statusCandidate = metadata?.status;
    const status =
        statusCandidate && isSupportedRecordStatus(statusCandidate)
            ? statusCandidate
            : chunkCount > 0
              ? 'saved'
              : 'recording';
    const recorderKindCandidate = metadata?.recorderKind;
    const recorderKind =
        recorderKindCandidate && isSupportedRecorderKind(recorderKindCandidate) ? recorderKindCandidate : fallbackKind;
    const updatedAt = typeof metadata?.updatedAt === 'number' ? metadata.updatedAt : startDate;

    return {
        uid: metadata?.uid || '',
        startDate,
        duration,
        chunkCount,
        status,
        recorderKind,
        updatedAt,
    };
};

const hasRecordPayload = (metadata: RecordMetadata) => metadata.chunkCount > 0 || metadata.duration > 0;

const isSessionDatabase = (database: {
    name?: string;
    version?: number;
}): database is { name: string; version: number } => {
    return Boolean(database.name && database.name.startsWith(SESSION_PREFIX) && (database.version || 0) >= 2);
};

const sortByUpdatedAtDesc = (a: SessionWithMetadata, b: SessionWithMetadata) => {
    return b.metadata.updatedAt - a.metadata.updatedAt;
};

const safeRemoveDatabases = async (dbNames: string[]): Promise<void> => {
    await Promise.all(
        dbNames.map(async (dbName) => {
            try {
                await removeDatabase(dbName);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Fail remove database ${dbName}`, error);
            }
        })
    );
};

const getSessionMetadata = async (sessionName: string): Promise<RecordMetadata | undefined> => {
    const db = new IndexedDatabase(sessionName, [ObjectStoreNames.Metadata], 2);
    try {
        const metadata = await db.get<Partial<RecordMetadata> | undefined>(ObjectStoreNames.Metadata, 'data');
        return normalizeMetadata(metadata, sessionName);
    } catch (error) {
        logger.error(error, { method: 'getSessionMetadata', additional: { sessionName } });
        return undefined;
    } finally {
        db.close();
    }
};

const setSessionMetadata = async (sessionName: string, metadata: RecordMetadata): Promise<void> => {
    const db = new IndexedDatabase(sessionName, [ObjectStoreNames.Metadata], 2);
    try {
        await db.put(ObjectStoreNames.Metadata, 'data', metadata);
    } finally {
        db.close();
    }
};

const markSessionDiscarded = async (sessionName: string, metadata?: Partial<RecordMetadata>) => {
    const normalizedMetadata = normalizeMetadata(metadata, sessionName);
    const discardedMetadata: RecordMetadata = {
        ...normalizedMetadata,
        status: 'discarded',
        updatedAt: Date.now(),
    };

    try {
        await setSessionMetadata(sessionName, discardedMetadata);
    } catch (error) {
        logger.error(error, { method: 'markSessionDiscarded' });
        await safeRemoveDatabases([sessionName]);
    }
};

const getVisibleSessions = async (sessionNames: string[], uid: string): Promise<SessionWithMetadata[]> => {
    const sessions = await Promise.all(
        sessionNames.map(async (name) => {
            const metadata = await getSessionMetadata(name);
            return { name, metadata };
        })
    );

    return sessions
        .filter((session): session is { name: string; metadata: RecordMetadata } => Boolean(session.metadata))
        .filter(({ metadata }) => metadata.uid === uid && metadata.status !== 'discarded' && hasRecordPayload(metadata))
        .sort(sortByUpdatedAtDesc);
};

/**
 * Сага, которая вызывает обновление информации о последней записи
 */
function* updateHasLastRecordInfo() {
    if (!indexedDB.databases) {
        return;
    }

    const uid = (yield* select(selectUserUid)) || '';
    if (!uid) {
        yield* put(setHasLastRecording(false));
        yield* put(setLastRecordingDate(undefined));
        return;
    }

    // Получаем список всех баз в indexedDB
    const databases = yield* call({ context: indexedDB, fn: indexedDB.databases });

    if (!databases.length) {
        yield* put(setHasLastRecording(false));
        yield* put(setLastRecordingDate(undefined));
        return;
    }

    const sessionNames = databases
        .filter(isSessionDatabase)
        .map(({ name }) => name)
        .sort();

    if (!sessionNames.length) {
        yield* put(setHasLastRecording(false));
        yield* put(setLastRecordingDate(undefined));
        return;
    }

    const visibleSessions = yield* call(getVisibleSessions, sessionNames, uid);
    const lastSession = visibleSessions[0];

    yield* put(setHasLastRecording(Boolean(lastSession)));
    yield* put(setLastRecordingDate(lastSession?.metadata.startDate));
}

/**
 * Сага, которая удаляет все записи
 */
export function* removeAllRecordingSessions() {
    if (!indexedDB.databases) {
        return;
    }

    const databases = yield* call({ context: indexedDB, fn: indexedDB.databases });
    const sessionDBs = databases.filter(({ name }) => name.startsWith(SESSION_PREFIX));

    yield* call(
        safeRemoveDatabases,
        sessionDBs.map(({ name }) => name)
    );

    yield* put(setHasLastRecording(false));
    yield* put(setLastRecordingDate(undefined));
}

/**
 * Сага, которая удаляет старые ненужные сессии, сохраняя только последнюю recoverable
 * или последнюю валидную запись текущего пользователя
 */
export function* cleanupRecordingSessions() {
    if (!indexedDB.databases) {
        return;
    }

    const uid = (yield* select(selectUserUid)) || '';
    const databases = yield* call({ context: indexedDB, fn: indexedDB.databases });
    const sessionNames = databases
        .filter(isSessionDatabase)
        .map(({ name }) => name)
        .sort();

    if (sessionNames.length === 0) {
        yield* put(setHasLastRecording(false));
        yield* put(setLastRecordingDate(undefined));
        return;
    }

    if (!uid) {
        yield* call(updateHasLastRecordInfo);
        return;
    }

    const visibleSessions = yield* call(getVisibleSessions, sessionNames, uid);
    if (!visibleSessions.length) {
        yield* call(updateHasLastRecordInfo);
        return;
    }

    const lastRecoverableSession = visibleSessions.find(({ metadata }) => metadata.status === 'recoverable');
    const sessionToPreserve = lastRecoverableSession?.name || visibleSessions[0]?.name;
    const toRemoveDbNames = visibleSessions.map(({ name }) => name).filter((name) => name !== sessionToPreserve);

    yield* call(safeRemoveDatabases, toRemoveDbNames);

    yield* call(updateHasLastRecordInfo);
}

export const fixRecordChunksAndDownload = async ({
    chunks,
    fileName,
    mimeType,
    duration,
}: {
    chunks: Blob[];
    fileName: string;
    mimeType: string;
    duration: number;
}) => {
    let blob = new Blob(chunks, { type: mimeType });

    // Фиксим продолжительность файла в хэдере
    try {
        // Для исправления продолжительности файла берем только самый первый чанк, который содержит всю
        // метаинформацию
        const toFixDurationBlob = new Blob(chunks.slice(0, 1), { type: mimeType });
        const fixedBlob = await fixWebmBlobDuration(toFixDurationBlob, duration);

        // Если удалось внести исправление, модифицированный первый кусок объединяем с остальными
        blob = new Blob([fixedBlob, ...chunks.slice(1)], { type: mimeType });
    } catch (error) {
        logger.error(error, { method: 'fixWebmDuration' });
    }

    const fixedObjectUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = fixedObjectUrl;
    a.download = fileName;
    a.click();
};

/**
 * Сага скачивания последней записи
 */
function* downloadLastRecordSaga(): SagaGenerator<void> {
    if (!indexedDB.databases) {
        return;
    }

    const uid = (yield* select(selectUserUid)) || '';
    if (!uid) {
        return;
    }

    // Получаем список всех баз в indexedDB
    const databases = yield* call({ context: indexedDB, fn: indexedDB.databases });

    const sessionNames = databases
        .filter(isSessionDatabase)
        .map(({ name }) => name)
        .sort();

    if (!sessionNames.length) {
        return;
    }

    const visibleSessions = yield* call(getVisibleSessions, sessionNames, uid);
    const lastSession = visibleSessions[0];
    if (!lastSession) {
        return;
    }

    const lastSessionName = lastSession.name;

    // Грузим всё содержимое записи, фиксим и скачиваем
    const db = new IndexedDatabase(lastSessionName, [ObjectStoreNames.Recording, ObjectStoreNames.Metadata], 2);
    let chunks: Blob[] = [];
    let metadataRaw: Partial<RecordMetadata> | undefined;
    try {
        chunks = (yield* call([db, 'getAll'], ObjectStoreNames.Recording)) as Blob[];
        metadataRaw = (yield* call([db, 'get'], ObjectStoreNames.Metadata, 'data')) as
            | Partial<RecordMetadata>
            | undefined;
    } finally {
        db.close();
    }

    const metadata = normalizeMetadata(metadataRaw, lastSessionName, lastSession.metadata.recorderKind);
    if (!chunks.length) {
        yield* call(markSessionDiscarded, lastSessionName, metadata);
        yield* call(updateHasLastRecordInfo);
        return;
    }

    const duration = metadata.duration || chunks.length * 1000;

    try {
        yield* call(fixRecordChunksAndDownload, {
            chunks,
            fileName: getRecordFileName(metadata.startDate),
            mimeType: RECORD_MIME_TYPE,
            duration,
        });

        const updatedMetadata: RecordMetadata = {
            ...metadata,
            duration,
            chunkCount: Math.max(metadata.chunkCount, chunks.length),
            status: 'saved',
            updatedAt: Date.now(),
        };
        yield* call(setSessionMetadata, lastSessionName, updatedMetadata);
    } catch (error) {
        logger.error(error, { method: 'downloadLastRecordSaga' });

        const updatedMetadata: RecordMetadata = {
            ...metadata,
            duration,
            chunkCount: Math.max(metadata.chunkCount, chunks.length),
            status: 'discarded',
            updatedAt: Date.now(),
        };
        yield* call(markSessionDiscarded, lastSessionName, updatedMetadata);
    }

    yield* call(updateHasLastRecordInfo);
}

/**
 * Рутовая сага для записи
 */
export function* rootDownloadRecordSaga() {
    if (NODE) {
        return;
    }

    yield all([
        call(cleanupRecordingSessions),
        takeEvery(updateHasLastRecordAction.type, updateHasLastRecordInfo),
        takeEvery(downloadLastRecordAction.type, downloadLastRecordSaga),
    ]);
}
